<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:show', 'send', 'cancel']);

const { t } = useI18n();

const latitude = ref('');
const longitude = ref('');
const name = ref('');
const address = ref('');
const errorMessage = ref('');
const isFetchingLocation = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const resetForm = () => {
  latitude.value = '';
  longitude.value = '';
  name.value = '';
  address.value = '';
  errorMessage.value = '';
  isFetchingLocation.value = false;
};

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) resetForm();
  }
);

const isValidCoordinate = (value, min, max) => {
  const parsed = Number.parseFloat(value);
  return !Number.isNaN(parsed) && parsed >= min && parsed <= max;
};

const validate = () => {
  if (!isValidCoordinate(latitude.value, -90, 90)) {
    errorMessage.value = t('CONVERSATION.REPLYBOX.LOCATION.INVALID_COORDINATES');
    return false;
  }
  if (!isValidCoordinate(longitude.value, -180, 180)) {
    errorMessage.value = t('CONVERSATION.REPLYBOX.LOCATION.INVALID_COORDINATES');
    return false;
  }
  errorMessage.value = '';
  return true;
};

const onClose = () => {
  emit('cancel');
  localShow.value = false;
};

const onSend = () => {
  if (!validate()) return;

  emit('send', {
    latitude: Number.parseFloat(latitude.value),
    longitude: Number.parseFloat(longitude.value),
    name: name.value.trim(),
    address: address.value.trim(),
  });
  localShow.value = false;
};

const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    useAlert(t('CONVERSATION.REPLYBOX.LOCATION.GEOLOCATION_UNAVAILABLE'));
    return;
  }

  isFetchingLocation.value = true;
  errorMessage.value = '';

  navigator.geolocation.getCurrentPosition(
    position => {
      latitude.value = String(position.coords.latitude);
      longitude.value = String(position.coords.longitude);
      isFetchingLocation.value = false;
    },
    () => {
      isFetchingLocation.value = false;
      useAlert(t('CONVERSATION.REPLYBOX.LOCATION.GEOLOCATION_ERROR'));
    }
  );
};
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    :on-close="onClose"
    size="modal-big"
  >
    <woot-modal-header
      :header-title="$t('CONVERSATION.REPLYBOX.LOCATION.MODAL_TITLE')"
      :header-content="$t('CONVERSATION.REPLYBOX.LOCATION.MODAL_SUBTITLE')"
    />
    <div class="flex flex-col gap-4 px-8 pb-8">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-model="latitude"
          type="number"
          :label="$t('CONVERSATION.REPLYBOX.LOCATION.LATITUDE_LABEL')"
          :placeholder="$t('CONVERSATION.REPLYBOX.LOCATION.LATITUDE_PLACEHOLDER')"
          :message="errorMessage"
          :message-type="errorMessage ? 'error' : 'info'"
        />
        <Input
          v-model="longitude"
          type="number"
          :label="$t('CONVERSATION.REPLYBOX.LOCATION.LONGITUDE_LABEL')"
          :placeholder="$t('CONVERSATION.REPLYBOX.LOCATION.LONGITUDE_PLACEHOLDER')"
        />
      </div>
      <Input
        v-model="name"
        :label="$t('CONVERSATION.REPLYBOX.LOCATION.NAME_LABEL')"
        :placeholder="$t('CONVERSATION.REPLYBOX.LOCATION.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="address"
        :label="$t('CONVERSATION.REPLYBOX.LOCATION.ADDRESS_LABEL')"
        :placeholder="$t('CONVERSATION.REPLYBOX.LOCATION.ADDRESS_PLACEHOLDER')"
      />
      <Button
        faded
        slate
        :label="$t('CONVERSATION.REPLYBOX.LOCATION.USE_CURRENT_LOCATION')"
        :is-loading="isFetchingLocation"
        @click="useCurrentLocation"
      />
      <div class="flex justify-end gap-2">
        <Button
          faded
          slate
          :label="$t('CONVERSATION.REPLYBOX.LOCATION.CANCEL')"
          @click="onClose"
        />
        <Button
          :label="$t('CONVERSATION.REPLYBOX.LOCATION.ADD_LOCATION')"
          @click="onSend"
        />
      </div>
    </div>
  </woot-modal>
</template>
